import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { financialAPI, projectsAPI } from '@/lib/api-client'
import { usePermission } from '@/hooks/usePermission'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChevronRight,
  Loader2,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Tag,
  Unlink,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

const ENTITY_TYPE_LABELS: Record<string, string> = {
  buyer: 'Comprador',
  investor: 'Investidor',
  supplier: 'Fornecedor',
  contractor: 'Empreiteiro',
  broker: 'Corretor',
}

const ENTITY_TYPE_COLORS: Record<string, string> = {
  buyer: 'bg-blue-100 text-blue-700',
  investor: 'bg-emerald-100 text-emerald-700',
  supplier: 'bg-amber-100 text-amber-700',
  contractor: 'bg-purple-100 text-purple-700',
  broker: 'bg-rose-100 text-rose-700',
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('pt-BR')
}

function formatMonth(month: string) {
  const [year, m] = month.split('-')
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  return `${months[parseInt(m) - 1]}/${year.slice(2)}`
}

const tooltipFormatter = (value: number) => formatCurrency(value)

// ==================== EntityCardList (left panel) ====================

interface EntityCardListProps {
  entities: any[]
  selectedEntityName: string | null
  onSelect: (name: string) => void
  isLoading: boolean
  onUnlinkAll?: (entityName: string) => void
  unlinkingEntity?: string | null
}

export function EntityCardList({
  entities,
  selectedEntityName,
  onSelect,
  isLoading,
  onUnlinkAll,
  unlinkingEntity,
}: EntityCardListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  if (!entities || entities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-12">
        <Users className="h-10 w-10 text-neutral-300" />
        <h3 className="mt-3 text-sm font-medium text-neutral-900">
          Nenhuma entidade conciliada
        </h3>
        <p className="mt-1 text-xs text-neutral-500 text-center">
          Concilie transações para vê-las agrupadas aqui.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-2">
      {entities.map((entity: any) => {
        const isSelected = selectedEntityName === entity.entityName

        const isUnlinking = unlinkingEntity === entity.entityName

        return (
          <Card
            key={entity.entityName}
            className={`cursor-pointer transition-all ${
              isSelected
                ? 'ring-2 ring-primary shadow-md'
                : 'hover:shadow-sm'
            }`}
            onClick={() => onSelect(entity.entityName)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate text-neutral-900">
                      {entity.entityName}
                    </span>
                    {entity.entityTypes.map((type: string) => (
                      <Badge
                        key={type}
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 ${ENTITY_TYPE_COLORS[type] || ''}`}
                      >
                        {ENTITY_TYPE_LABELS[type] || type}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-1.5 flex items-center gap-4 text-xs">
                    {entity.totalIncome > 0 && (
                      <div className="flex items-center gap-1 text-green-600">
                        <ArrowUpRight className="h-3.5 w-3.5" />
                        <span className="font-medium">
                          {formatCurrency(entity.totalIncome)}
                        </span>
                      </div>
                    )}
                    {entity.totalExpense > 0 && (
                      <div className="flex items-center gap-1 text-red-600">
                        <ArrowDownRight className="h-3.5 w-3.5" />
                        <span className="font-medium">
                          {formatCurrency(entity.totalExpense)}
                        </span>
                      </div>
                    )}
                    <span className="text-neutral-400">
                      {entity.transactionCount} tx
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {onUnlinkAll && (
                    <button
                      className="rounded p-1 text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); onUnlinkAll(entity.entityName) }}
                      title="Desvincular agrupamento"
                      disabled={isUnlinking}
                    >
                      {isUnlinking
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Unlink className="h-3.5 w-3.5" />
                      }
                    </button>
                  )}
                  {isSelected && (
                    <ChevronRight className="h-4 w-4 text-primary" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

// ==================== EntityDetail (right panel / sheet) ====================

interface EntityDetailProps {
  entity: any
}

export function EntityDetail({ entity }: EntityDetailProps) {
  const canEdit = usePermission('financial:edit')
  const queryClient = useQueryClient()

  const [selectedTxId, setSelectedTxId] = useState<string | null>(null)
  const [catType, setCatType] = useState<'project_expense' | 'operational_expense' | ''>('')
  const [catProjectId, setCatProjectId] = useState('')
  const [catCategoryName, setCatCategoryName] = useState('')

  const selectedTx = entity.transactions.find((tx: any) => tx.id === selectedTxId)

  const { data: catProjectsData } = useQuery({
    queryKey: ['projects', { limit: 100 }],
    queryFn: () => projectsAPI.list({ limit: 100 }),
    enabled: catType === 'project_expense',
  })

  const { data: catExpenseCategories } = useQuery({
    queryKey: ['expense-categories', catType],
    queryFn: () => financialAPI.listFinancialCategories(catType === 'project_expense' ? 'OBRA' : 'OPERACIONAL'),
    enabled: catType === 'project_expense' || catType === 'operational_expense',
  })

  const unlinkMutation = useMutation({
    mutationFn: () => financialAPI.unlinkTransaction(selectedTxId!),
    onSuccess: () => {
      toast.success('Transação desvinculada.')
      queryClient.invalidateQueries({ queryKey: ['reconciliation-entities-summary'] })
      setSelectedTxId(null)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const categorizeMutation = useMutation({
    mutationFn: () =>
      financialAPI.updateTransaction(selectedTxId!, {
        category: catCategoryName,
        ...(catType === 'project_expense' && catProjectId ? { projectId: catProjectId } : {}),
      }),
    onSuccess: () => {
      toast.success('Transação categorizada!')
      queryClient.invalidateQueries({ queryKey: ['reconciliation-entities-summary'] })
      setCatType('')
      setCatProjectId('')
      setCatCategoryName('')
      setSelectedTxId(null)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  function handleRowClick(txId: string) {
    setSelectedTxId(txId)
    setCatType('')
    setCatProjectId('')
    setCatCategoryName('')
  }

  function handleDialogClose(open: boolean) {
    if (!open) {
      setSelectedTxId(null)
      setCatType('')
      setCatProjectId('')
      setCatCategoryName('')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-semibold text-neutral-900">
            {entity.entityName}
          </h3>
          {entity.entityTypes.map((type: string) => (
            <Badge
              key={type}
              variant="outline"
              className={ENTITY_TYPE_COLORS[type] || ''}
            >
              {ENTITY_TYPE_LABELS[type] || type}
            </Badge>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-6 text-sm">
          {entity.totalIncome > 0 && (
            <div className="flex items-center gap-1 text-green-600">
              <ArrowUpRight className="h-4 w-4" />
              <span className="font-medium">
                {formatCurrency(entity.totalIncome)}
              </span>
            </div>
          )}
          {entity.totalExpense > 0 && (
            <div className="flex items-center gap-1 text-red-600">
              <ArrowDownRight className="h-4 w-4" />
              <span className="font-medium">
                {formatCurrency(entity.totalExpense)}
              </span>
            </div>
          )}
          <span className="text-neutral-500">
            {entity.transactionCount} transação(ões)
          </span>
        </div>
      </div>

      {/* Bar Chart - Cash Flow */}
      {entity.timeseries.length > 1 && (
        <div>
          <h4 className="mb-3 text-sm font-medium text-neutral-700">
            Fluxo de Caixa
          </h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={entity.timeseries}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tickFormatter={formatMonth}
                fontSize={11}
              />
              <YAxis
                fontSize={11}
                tickFormatter={(v) =>
                  new Intl.NumberFormat('pt-BR', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(v)
                }
              />
              <Tooltip
                formatter={tooltipFormatter}
                labelFormatter={formatMonth}
              />
              <Legend />
              <Bar
                dataKey="income"
                fill="#10b981"
                name="Entradas"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="expense"
                fill="#ef4444"
                name="Saídas"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Transactions Table */}
      <div>
        <h4 className="mb-3 text-sm font-medium text-neutral-700">Transações</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead className="text-right">Valor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entity.transactions.map((tx: any) => (
              <TableRow
                key={tx.id}
                onClick={() => canEdit && handleRowClick(tx.id)}
                className={canEdit ? 'cursor-pointer' : ''}
              >
                <TableCell className="text-sm">{formatDate(tx.date)}</TableCell>
                <TableCell className="text-sm max-w-[300px] truncate">{tx.description}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={tx.type === 'INCOME' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                  >
                    {tx.type === 'INCOME' ? 'Entrada' : 'Saída'}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-neutral-500">
                  {tx.category && !['Receita Importada', 'Despesa Importada'].includes(tx.category)
                    ? tx.category
                    : <span className="text-neutral-300">—</span>
                  }
                </TableCell>
                <TableCell className="text-right text-sm font-medium">
                  <span className={tx.type === 'INCOME' ? 'text-green-600' : 'text-red-600'}>
                    {tx.type === 'INCOME' ? '+' : '-'}{formatCurrency(tx.amount)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog: Categorizar Despesa */}
      <Dialog open={!!selectedTx && canEdit} onOpenChange={handleDialogClose}>
        <DialogContent
          className="sm:max-w-md overflow-hidden"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="pr-6">
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Categorizar Despesa
            </DialogTitle>
            {selectedTx && (
              <div className="mt-2 space-y-0.5 overflow-hidden">
                <p className="text-sm text-neutral-600 break-words line-clamp-2">
                  {selectedTx.description}
                </p>
                <div className="flex items-center gap-3">
                  <span className={`text-base font-semibold ${selectedTx.type === 'INCOME' ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedTx.type === 'INCOME' ? '+' : '-'}{formatCurrency(selectedTx.amount)}
                  </span>
                  {selectedTx.category && !['Receita Importada', 'Despesa Importada'].includes(selectedTx.category) && (
                    <span className="text-xs text-neutral-400 truncate">
                      {selectedTx.category}
                      {selectedTx.project && ` · ${selectedTx.project.name}`}
                    </span>
                  )}
                </div>
              </div>
            )}
          </DialogHeader>

          {/* Desvincular — só aparece se a transação tiver vínculo ativo */}
          {selectedTx?.linkedEntityName && (
            <>
              <Separator />
              <div className="flex items-center justify-between gap-3 rounded-md bg-neutral-50 border px-3 py-2">
                <div className="min-w-0 overflow-hidden">
                  <p className="text-xs text-neutral-500">Vinculada a</p>
                  <p className="text-sm font-medium text-neutral-800 truncate">{selectedTx.linkedEntityName}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => unlinkMutation.mutate()}
                  disabled={unlinkMutation.isPending}
                >
                  {unlinkMutation.isPending
                    ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    : <Unlink className="mr-1 h-3.5 w-3.5" />
                  }
                  Desvincular
                </Button>
              </div>
            </>
          )}

          <Separator />

          <div className="space-y-3">
            <Select
              value={catType}
              onValueChange={(v) => { setCatType(v as any); setCatProjectId(''); setCatCategoryName('') }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tipo de despesa..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="project_expense">Despesa de Obra</SelectItem>
                <SelectItem value="operational_expense">Despesa Operacional</SelectItem>
              </SelectContent>
            </Select>

            {catType === 'project_expense' && (
              <Select value={catProjectId} onValueChange={setCatProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o projeto..." />
                </SelectTrigger>
                <SelectContent>
                  {((catProjectsData as any)?.data || []).map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {catType && (
              <Select value={catCategoryName} onValueChange={setCatCategoryName}>
                <SelectTrigger>
                  <SelectValue placeholder="Subcategoria..." />
                </SelectTrigger>
                <SelectContent>
                  {((catExpenseCategories as any[]) || []).map((c: any) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogClose(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => categorizeMutation.mutate()}
              disabled={
                !catType ||
                !catCategoryName ||
                (catType === 'project_expense' && !catProjectId) ||
                categorizeMutation.isPending
              }
            >
              {categorizeMutation.isPending
                ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                : <Tag className="mr-1 h-4 w-4" />
              }
              Categorizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
